import React from 'react';
import {
  CheckCircle,
  Info,
  Lightbulb,
  ChevronRight,
  Send,
  Settings,
  HelpCircle,
  Clock,
  FileText,
} from 'lucide-react';

// Reusable components from ManagingDashboard
const ExampleBox = ({ children }) => (
  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded-r-lg">
    <div className="flex items-start">
      <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-sm font-semibold text-blue-800 mb-1">Example</p>
        <div className="text-blue-700 text-sm">{children}</div>
      </div>
    </div>
  </div>
);

const ProTip = ({ children }) => (
  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded-r-lg">
    <div className="flex items-start">
      <Lightbulb className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-sm font-semibold text-yellow-800 mb-1">Pro Tip</p>
        <div className="text-yellow-700 text-sm">{children}</div>
      </div>
    </div>
  </div>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="url(#instagram-gradient)">
    <defs>
      <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f09433" />
        <stop offset="25%" stopColor="#e6683c" />
        <stop offset="50%" stopColor="#dc2743" />
        <stop offset="75%" stopColor="#cc2366" />
        <stop offset="100%" stopColor="#bc1888" />
      </linearGradient>
    </defs>
    <path d="M16.98 0a6.9 6.9 0 0 1 6.9 6.9v10.2a6.9 6.9 0 0 1-6.9 6.9H7.02a6.9 6.9 0 0 1-6.9-6.9V6.9A6.9 6.9 0 0 1 7.02 0h9.96zM12 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.488" />
  </svg>
);

const Support = () => {
  return (
    <div className="min-h-screen bg-gray-50 mt-[32px]">
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="bg-blue-100 rounded-lg p-2 mr-4">
              <HelpCircle className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Support: Help Is Just a Click Away
            </h1>
          </div>
          <p className="text-gray-600 mb-6">
            At ChatInject, we’re committed to ensuring your experience is smooth, efficient, and hassle-free. Our support options are designed to provide prompt and effective assistance across all connected platforms.
          </p>
          <div className="flex space-x-2 mb-6">
            <FacebookIcon />
            <InstagramIcon />
            <WhatsAppIcon />
          </div>

          {/* Contact Support */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Support: Submit a Ticket with Ease</h2>
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Settings className="w-5 h-5 mr-2 text-gray-600" />
                  Open the Support Tab
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-blue-500" />
                    Navigate to the sidebar and click on <strong>Support</strong>.
                  </li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-600" />
                  Submit the Ticket Form
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    '<strong>Issue Title:</strong> A brief description of the problem (e.g., "Unable to connect Instagram account").',
                    '<strong>Detailed Description:</strong> Explain the issue in detail to help our team understand and resolve it faster.',
                    '<strong>Attach Files (Optional):</strong> Upload screenshots or logs to assist in diagnosing the problem.',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-blue-500" />
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
                <ExampleBox>
                  Submit a ticket with the title “Login Issue” and include a screenshot of the error message for faster resolution.
                </ExampleBox>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Send className="w-5 h-5 mr-2 text-gray-600" />
                  Submit and Track
                </h3>
                <ol className="space-y-3 text-gray-600">
                  {[
                    'Click <strong>Submit</strong>, and your ticket will be sent directly to our support team.',
                    'You’ll receive a confirmation email with a ticket ID for tracking the progress of your request.',
                  ].map((step, index) => (
                    <li key={index} className="flex items-start">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                        {index + 1}
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: step }} />
                    </li>
                  ))}
                </ol>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-green-600" />
                  Resolution Time
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-green-500" />
                    Our team is committed to resolving all support tickets within <strong>24 hours</strong>. If the issue is complex, you’ll be updated regularly until it’s resolved.
                  </li>
                </ul>
                <ProTip>
                  Include as much detail as possible in your ticket description to help our team resolve your issue faster.
                </ProTip>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Benefits of the Support System</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Convenient Access',
                  description: 'Submit tickets directly from your ChatInject account.',
                  icon: Send,
                },
                {
                  title: 'Fast Resolution',
                  description: 'Most issues are addressed within 24 hours.',
                  icon: Clock,
                },
                {
                  title: 'Track Progress',
                  description: 'Monitor the status of your ticket for complete transparency.',
                  icon: FileText,
                },
              ].map((benefit, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6 text-center">
                  <div className="bg-white rounded-lg p-4 mb-3 shadow-sm mx-auto w-16 h-16 flex items-center justify-center">
                    <benefit.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>
            <ExampleBox>
              Track your ticket status in real-time to stay updated on resolution progress without needing to follow up manually.
            </ExampleBox>
          </section>

          {/* Footer */}
          <footer className="bg-white rounded-lg shadow-sm p-6 text-center">
            <p className="text-gray-600 mb-4">
              Need additional help? Our support team is here to assist you with any questions about using ChatInject.
            </p>
            <div className="flex justify-center space-x-4 text-sm">
              <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Support Center</a>
              <span className="text-gray-300">|</span>
              <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Video Tutorials</a>
              <span className="text-gray-300">|</span>
              <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Contact Us</a>
            </div>
          </footer>
        </div>
    </div>
  );
};

export default Support;